"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import {
  absoluteHumidity,
  MAGNUS_WATER_RANGE_C,
  PSYCHROMETER_K,
  relativeHumidityFromDewPoint,
  relativeHumidityFromVaporPressure,
  saturationTemperature,
  saturationVaporPressure,
  vaporPressureFromWetBulb,
} from "@/lib/tool-kit/earth-science/humidity";
import {
  fromHectopascals,
  type PressureUnit,
  toHectopascals,
} from "@/lib/tool-kit/earth-science/pressure";
import {
  differenceFromCelsius,
  fromCelsius,
  type TemperatureUnit,
  toCelsius,
  unitSymbol,
} from "@/lib/tool-kit/earth-science/temperature";

/** English strings. A translated content file overrides these through `ui`. */
export const RELATIVE_HUMIDITY_CALCULATOR_STRINGS = {
  modeLabel: "What readings do you have?",
  modeDew: "Air temperature and dew point",
  modeWet: "Dry-bulb and wet-bulb (psychrometer)",
  unitLabel: "Temperature unit",
  unitC: "Celsius (°C)",
  unitF: "Fahrenheit (°F)",
  unitK: "Kelvin (K)",
  dryLabel: "Air (dry-bulb) temperature",
  dewLabel: "Dew point",
  wetLabel: "Wet-bulb temperature",
  pressureLabel: "Air pressure",
  pressureUnitLabel: "Pressure unit",
  pressureHpa: "hPa",
  pressureKpa: "kPa",
  pressureInHg: "inHg",
  pressureMmHg: "mmHg",
  pressureHint: "Station pressure. Sea level is 1013.25 hPa (29.92 inHg).",
  iceLabel: "The wet bulb is coated with ice (below freezing)",
  results: "Results",
  humidity: "Relative humidity",
  dewPoint: "Dew point",
  vaporPressure: "Vapor pressure e (hPa)",
  saturationPressure: "Saturation vapor pressure (hPa)",
  absoluteHumidity: "Absolute humidity (g/m³)",
  depressionDew: "Dew point depression",
  depressionWet: "Wet-bulb depression",
  workingHeading: "The working",
  stepDewSaturation:
    "Saturation vapor pressure at the dew point {td} °C: es(Td) = {esd} hPa.",
  stepAirSaturation:
    "Saturation vapor pressure at the air temperature {t} °C: es(T) = {es} hPa.",
  stepDewRatio:
    "Relative humidity = es(Td) / es(T) × 100 = {esd} / {es} × 100 = {rh} %.",
  stepWetSaturation:
    "Saturation vapor pressure at the wet bulb {tw} °C: es(Tw) = {esw} hPa.",
  stepCorrection:
    "Psychrometer term: {k} × (1 + 0.00115 × {tw}) × {p} hPa × ({t} − {tw}) = {term} hPa.",
  stepVapor: "Vapor pressure: e = {esw} − {term} = {e} hPa.",
  stepWetRatio:
    "Relative humidity = e / es(T) × 100 = {e} / {es} × 100 = {rh} %.",
  tableDewHeading: "Humidity for other dew point depressions at {temperature}",
  tableWetHeading: "Psychrometric table at {temperature} and {pressure}",
  columnDepressionDew: "Dew point depression",
  columnDepressionWet: "Wet-bulb depression",
  columnHumidity: "Relative humidity",
  columnDewPoint: "Dew point",
  psychrometerNote:
    "The psychrometer equation of Ferrel (Smithsonian Meteorological Tables) is for a ventilated wet bulb — a sling or aspirated psychrometer in air moving at about 3 m/s. An unventilated wet bulb reads too high and gives too much humidity.",
  magnusNote:
    "Saturation vapor pressure by the Magnus formula (Alduchov and Eskridge, 1996), fitted for −40 to 60 °C.",
  outOfRange:
    "The Magnus coefficients are fitted for −40 to 60 °C; at {temperature} the result is a rougher estimate.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine:
    "Relative humidity {rh} % at {temperature} (dew point {dew}, vapor pressure {e} hPa)",
  enterValues: "Enter the readings above to see the relative humidity.",
  dewTooHigh:
    "The dew point cannot be higher than the air temperature — that would be more than 100 % humidity. Check the two readings.",
  wetTooHigh:
    "The wet-bulb reading cannot be higher than the dry-bulb reading. Check which thermometer is which.",
  noVapor:
    "These readings imply almost no water vapor at this pressure; a real psychrometer would not read that far apart. Check the numbers.",
  pressureInvalid: "Enter an air pressure above zero.",
  belowZero:
    "That temperature is below absolute zero in the chosen unit. Check the number and the unit.",
} as const;

type Strings = typeof RELATIVE_HUMIDITY_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "dew" | "wet";

const PRESSURE_KEYS = {
  hPa: "pressureHpa",
  kPa: "pressureKpa",
  inHg: "pressureInHg",
  mmHg: "pressureMmHg",
} as const;

const TWO = { maximumFractionDigits: 2 };
const THREE = { maximumFractionDigits: 3 };
const ONE = { maximumFractionDigits: 1 };

/** Depressions for the lookup table, in the reader's own unit. */
const DEPRESSIONS: Record<TemperatureUnit, number[]> = {
  C: [1, 2, 3, 4, 5, 6, 8, 10],
  K: [1, 2, 3, 4, 5, 6, 8, 10],
  F: [2, 4, 6, 8, 10, 12, 15, 20],
};

function degrees(fmt: Fmt, celsius: number, unit: TemperatureUnit): string {
  return `${fmt(fromCelsius(celsius, unit), ONE)} ${unitSymbol(unit)}`;
}

/** The size of a depression given in the reader's unit, in kelvin. */
function toKelvinStep(step: number, unit: TemperatureUnit): number {
  return unit === "F" ? (step * 5) / 9 : step;
}

interface Reading {
  dry: number;
  /** Dew point (dew mode) or wet-bulb (wet mode), °C. */
  second: number;
  pressureHpa: number;
  ice: boolean;
}

interface Solved {
  rh: number;
  e: number;
  es: number;
  dew: number;
  steps: string[];
}

function solveDew(reading: Reading, strings: Strings, fmt: Fmt): Solved {
  const es = saturationVaporPressure(reading.dry);
  const esd = saturationVaporPressure(reading.second);
  const rh = relativeHumidityFromDewPoint(reading.dry, reading.second);
  return {
    rh,
    e: esd,
    es,
    dew: reading.second,
    steps: [
      t(strings.stepDewSaturation, {
        td: fmt(reading.second, TWO),
        esd: fmt(esd, THREE),
      }),
      t(strings.stepAirSaturation, {
        t: fmt(reading.dry, TWO),
        es: fmt(es, THREE),
      }),
      t(strings.stepDewRatio, {
        esd: fmt(esd, THREE),
        es: fmt(es, THREE),
        rh: fmt(rh, TWO),
      }),
    ],
  };
}

function solveWet(reading: Reading, strings: Strings, fmt: Fmt): Solved | null {
  const { dry, second: wet, pressureHpa, ice } = reading;
  const phase = ice ? "ice" : "water";
  const esw = saturationVaporPressure(wet, phase);
  const e = vaporPressureFromWetBulb(dry, wet, pressureHpa, ice);
  if (!(e > 0)) {
    return null;
  }
  const es = saturationVaporPressure(dry);
  const term = esw - e;
  const rh = relativeHumidityFromVaporPressure(dry, e);
  return {
    rh,
    e,
    es,
    dew: saturationTemperature(e, "water"),
    steps: [
      t(strings.stepWetSaturation, { tw: fmt(wet, TWO), esw: fmt(esw, THREE) }),
      t(strings.stepCorrection, {
        k: PSYCHROMETER_K[phase].toExponential(2).replace("e-4", " × 10⁻⁴"),
        tw: fmt(wet, TWO),
        p: fmt(pressureHpa, TWO),
        t: fmt(dry, TWO),
        term: fmt(term, THREE),
      }),
      t(strings.stepVapor, {
        esw: fmt(esw, THREE),
        term: fmt(term, THREE),
        e: fmt(e, THREE),
      }),
      t(strings.stepWetRatio, {
        e: fmt(e, THREE),
        es: fmt(es, THREE),
        rh: fmt(rh, TWO),
      }),
    ],
  };
}

function tableRows(
  mode: Mode,
  reading: Reading,
  unit: TemperatureUnit,
  fmt: Fmt
): string[][] {
  const rows: string[][] = [];
  for (const step of DEPRESSIONS[unit]) {
    const second = reading.dry - toKelvinStep(step, unit);
    if (mode === "dew") {
      const rh = relativeHumidityFromDewPoint(reading.dry, second);
      rows.push([
        `${fmt(step)} ${unitSymbol(unit)}`,
        `${fmt(rh, ONE)} %`,
        degrees(fmt, second, unit),
      ]);
      continue;
    }
    const e = vaporPressureFromWetBulb(
      reading.dry,
      second,
      reading.pressureHpa,
      reading.ice && second < 0
    );
    if (e > 0) {
      rows.push([
        `${fmt(step)} ${unitSymbol(unit)}`,
        `${fmt(relativeHumidityFromVaporPressure(reading.dry, e), ONE)} %`,
        degrees(fmt, saturationTemperature(e, "water"), unit),
      ]);
    }
  }
  return rows;
}

function Results({
  mode,
  reading,
  unit,
  pressureUnit,
  strings,
  fmt,
}: {
  mode: Mode;
  reading: Reading;
  unit: TemperatureUnit;
  pressureUnit: PressureUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const solved =
    mode === "dew"
      ? solveDew(reading, strings, fmt)
      : solveWet(reading, strings, fmt);
  if (solved === null) {
    return <ErrorNotice>{strings.noVapor}</ErrorNotice>;
  }
  const dryText = degrees(fmt, reading.dry, unit);
  const outside =
    reading.dry < MAGNUS_WATER_RANGE_C.min ||
    reading.dry > MAGNUS_WATER_RANGE_C.max;
  const depression = differenceFromCelsius(reading.dry - reading.second, unit);
  const pressureText = `${fmt(fromHectopascals(reading.pressureHpa, pressureUnit), TWO)} ${strings[PRESSURE_KEYS[pressureUnit]]}`;
  return (
    <>
      {outside ? (
        <Notice>{t(strings.outOfRange, { temperature: dryText })}</Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.humidity}
          value={`${fmt(solved.rh, ONE)} %`}
        />
        <StatCard
          label={strings.dewPoint}
          value={degrees(fmt, solved.dew, unit)}
        />
        <StatCard
          label={mode === "dew" ? strings.depressionDew : strings.depressionWet}
          value={`${fmt(depression, ONE)} ${unitSymbol(unit)}`}
        />
        <StatCard label={strings.vaporPressure} value={fmt(solved.e, TWO)} />
        <StatCard
          label={strings.saturationPressure}
          value={fmt(solved.es, TWO)}
        />
        <StatCard
          label={strings.absoluteHumidity}
          value={fmt(absoluteHumidity(solved.e, reading.dry), ONE)}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLine, {
          rh: fmt(solved.rh, ONE),
          temperature: dryText,
          dew: degrees(fmt, solved.dew, unit),
          e: fmt(solved.e, TWO),
        })}
      />
      <Panel title={strings.workingHeading}>
        <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
          {solved.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Panel>
      <h3 className={WIDGET_H3}>
        {mode === "dew"
          ? t(strings.tableDewHeading, { temperature: dryText })
          : t(strings.tableWetHeading, {
              temperature: dryText,
              pressure: pressureText,
            })}
      </h3>
      <DataTable
        head={[
          mode === "dew"
            ? strings.columnDepressionDew
            : strings.columnDepressionWet,
          strings.columnHumidity,
          strings.columnDewPoint,
        ]}
        rows={tableRows(mode, reading, unit, fmt)}
      />
      <p className={HINT}>
        {mode === "wet" ? `${strings.psychrometerNote} ` : ""}
        {strings.magnusNote}
      </p>
    </>
  );
}

interface Inputs {
  dry: string;
  second: string;
  pressure: string;
  ice: boolean;
}

function Outcome({
  mode,
  inputs,
  unit,
  pressureUnit,
  strings,
  fmt,
}: {
  mode: Mode;
  inputs: Inputs;
  unit: TemperatureUnit;
  pressureUnit: PressureUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const dryTyped = parseScientificInput(inputs.dry);
  const secondTyped = parseScientificInput(inputs.second);
  const pressureTyped = parseScientificInput(inputs.pressure);
  if (dryTyped === null || secondTyped === null) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  const dry = toCelsius(dryTyped, unit);
  const second = toCelsius(secondTyped, unit);
  if (dry < -273.15 || second < -273.15) {
    return <ErrorNotice>{strings.belowZero}</ErrorNotice>;
  }
  if (mode === "wet" && !(pressureTyped !== null && pressureTyped > 0)) {
    return <ErrorNotice>{strings.pressureInvalid}</ErrorNotice>;
  }
  if (second > dry + 1e-9) {
    return (
      <ErrorNotice>
        {mode === "dew" ? strings.dewTooHigh : strings.wetTooHigh}
      </ErrorNotice>
    );
  }
  return (
    <Results
      fmt={fmt}
      mode={mode}
      pressureUnit={pressureUnit}
      reading={{
        dry,
        second,
        pressureHpa: toHectopascals(pressureTyped ?? 1013.25, pressureUnit),
        ice: inputs.ice && second < 0,
      }}
      strings={strings}
      unit={unit}
    />
  );
}

export default function RelativeHumidityCalculatorWidget() {
  const strings = useToolUi(RELATIVE_HUMIDITY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("dew");
  const [unit, setUnit] = useState<TemperatureUnit>("C");
  const [dry, setDry] = useState("25");
  const [dew, setDew] = useState("18");
  const [wet, setWet] = useState("20");
  const [pressure, setPressure] = useState("1013.25");
  const [pressureUnit, setPressureUnit] = useState<PressureUnit>("hPa");
  const [ice, setIce] = useState(false);

  function changePressureUnit(next: PressureUnit) {
    const typed = parseScientificInput(pressure);
    if (typed !== null) {
      const converted = fromHectopascals(
        toHectopascals(typed, pressureUnit),
        next
      );
      setPressure(String(Math.round(converted * 100) / 100));
    }
    setPressureUnit(next);
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="rhc-mode"
        onChange={setMode}
        options={[
          { value: "dew", label: strings.modeDew },
          { value: "wet", label: strings.modeWet },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="rhc-dry"
          label={strings.dryLabel}
          numeric
          onChange={setDry}
          value={dry}
        />
        <TextField
          id="rhc-second"
          label={mode === "dew" ? strings.dewLabel : strings.wetLabel}
          numeric
          onChange={mode === "dew" ? setDew : setWet}
          value={mode === "dew" ? dew : wet}
        />
        <SelectField
          id="rhc-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={[
            { value: "C", label: strings.unitC },
            { value: "F", label: strings.unitF },
            { value: "K", label: strings.unitK },
          ]}
          value={unit}
        />
      </div>
      {mode === "wet" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.pressureHint}
              id="rhc-pressure"
              label={strings.pressureLabel}
              numeric
              onChange={setPressure}
              value={pressure}
            />
            <SelectField
              id="rhc-pressure-unit"
              label={strings.pressureUnitLabel}
              onChange={changePressureUnit}
              options={[
                { value: "hPa", label: strings.pressureHpa },
                { value: "kPa", label: strings.pressureKpa },
                { value: "inHg", label: strings.pressureInHg },
                { value: "mmHg", label: strings.pressureMmHg },
              ]}
              value={pressureUnit}
            />
          </div>
          <Checkbox checked={ice} label={strings.iceLabel} onChange={setIce} />
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          fmt={fmt}
          inputs={{
            dry,
            second: mode === "dew" ? dew : wet,
            pressure,
            ice,
          }}
          mode={mode}
          pressureUnit={pressureUnit}
          strings={strings}
          unit={unit}
        />
      </section>
    </div>
  );
}
